import { apiService } from '../../../components/api/apiService';
import {
  CatalogApp,
  CatalogAppFormData,
  CatalogAvailability,
  CatalogCopilotOption,
  CatalogStatus,
} from './catalogTypes';
import { logCatalog } from './catalogLogger';

const CONNECTION = 'backend_py';
const BASE = '/api/v1/catalog/apps';
const COPILOTS_BASE = '/api/v1/catalog/copilots';

function catalogErrorMessage(err: unknown): string {
  const axiosErr = err as {
    response?: { data?: { error?: { message?: string }; message?: string }; status?: number };
    message?: string;
  };
  return (
    axiosErr.response?.data?.error?.message ||
    axiosErr.response?.data?.message ||
    axiosErr.message ||
    'Catalog request failed'
  );
}

function asStatus(value: unknown): CatalogStatus {
  if (value === 'inactive' || value === 'maintenance' || value === 'active') return value;
  return 'active';
}

function asAvailability(value: unknown): CatalogAvailability {
  if (value === 'copilot' || value === 'standalone' || value === 'both') return value;
  return 'both';
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const item of value) {
    const text = String(item ?? '').trim();
    if (!text || seen.has(text)) continue;
    seen.add(text);
    result.push(text);
  }
  return result;
}

function mapApp(raw: Record<string, unknown>): CatalogApp {
  return {
    id: String(raw.id ?? ''),
    name: String(raw.name ?? ''),
    description: String(raw.description ?? ''),
    icon: String(raw.icon ?? 'Boxes'),
    iconColor: String(raw.iconColor ?? raw.icon_color ?? 'indigo'),
    iconUrl: String(raw.iconUrl ?? raw.icon_url ?? ''),
    dns: String(raw.dns ?? ''),
    url: String(raw.url ?? ''),
    status: asStatus(raw.status),
    appCode: String(raw.appCode ?? raw.app_code ?? ''),
    ownerId: String(raw.ownerId ?? raw.owner_id ?? ''),
    availability: asAvailability(raw.availability),
    copilotIds: asStringArray(raw.copilotIds ?? raw.copilot_ids),
  };
}

function mapCopilot(raw: Record<string, unknown>): CatalogCopilotOption {
  return {
    agentId: String(raw.agentId ?? raw.agent_id ?? ''),
    agentTitle: String(raw.agentTitle ?? raw.agent_title ?? ''),
    description: String(raw.description ?? ''),
    agentIconUrl: String(raw.agentIconUrl ?? raw.agent_icon_url ?? ''),
  };
}

function toPayload(data: CatalogAppFormData): Record<string, unknown> {
  return {
    name: data.name,
    description: data.description,
    icon: data.icon,
    iconColor: data.iconColor || 'indigo',
    iconUrl: data.iconUrl || '',
    dns: data.dns,
    url: data.url,
    status: data.status,
    appCode: data.appCode,
    ownerId: data.ownerId,
    availability: data.availability,
    copilotIds: asStringArray(data.copilotIds),
  };
}

export const catalogService = {
  async list(): Promise<CatalogApp[]> {
    logCatalog('app', 'list_catalog_apps');
    try {
      const data = (await apiService.get(BASE, {}, CONNECTION)) as Record<string, unknown>;
      const items = Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [];
      const apps = items.map((item) => mapApp(item as Record<string, unknown>));
      logCatalog('app', 'list_catalog_apps_ok', { count: apps.length });
      return apps;
    } catch (err) {
      const message = catalogErrorMessage(err);
      logCatalog('error', 'list_catalog_apps_failed', { message });
      throw new Error(message);
    }
  },

  async listCopilots(): Promise<CatalogCopilotOption[]> {
    logCatalog('app', 'list_catalog_copilots');
    try {
      const data = (await apiService.get(COPILOTS_BASE, {}, CONNECTION)) as Record<string, unknown>;
      const items = Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [];
      const copilots = items
        .map((item) => mapCopilot(item as Record<string, unknown>))
        .filter((c) => c.agentId);
      logCatalog('app', 'list_catalog_copilots_ok', { count: copilots.length });
      return copilots;
    } catch (err) {
      const message = catalogErrorMessage(err);
      logCatalog('error', 'list_catalog_copilots_failed', { message });
      throw new Error(message);
    }
  },

  async create(payload: CatalogAppFormData): Promise<CatalogApp> {
    logCatalog('app', 'create_catalog_app', {
      appCode: payload.appCode,
      copilots: payload.copilotIds?.length ?? 0,
    });
    try {
      const data = (await apiService.post(BASE, toPayload(payload), CONNECTION)) as Record<
        string,
        unknown
      >;
      const app = mapApp(data);
      logCatalog('app', 'create_catalog_app_ok', { id: app.id, appCode: app.appCode });
      return app;
    } catch (err) {
      const message = catalogErrorMessage(err);
      logCatalog('error', 'create_catalog_app_failed', { message, appCode: payload.appCode });
      throw new Error(message);
    }
  },

  async update(id: string, payload: CatalogAppFormData): Promise<CatalogApp> {
    logCatalog('app', 'update_catalog_app', {
      id,
      appCode: payload.appCode,
      copilots: payload.copilotIds?.length ?? 0,
    });
    try {
      const data = (await apiService.put(`${BASE}/${id}`, toPayload(payload), CONNECTION)) as Record<
        string,
        unknown
      >;
      const app = mapApp(data);
      logCatalog('app', 'update_catalog_app_ok', { id: app.id, appCode: app.appCode });
      return app;
    } catch (err) {
      const message = catalogErrorMessage(err);
      logCatalog('error', 'update_catalog_app_failed', { message, id });
      throw new Error(message);
    }
  },

  async remove(id: string): Promise<void> {
    logCatalog('app', 'delete_catalog_app', { id });
    try {
      await apiService.delete(`${BASE}/${id}`, CONNECTION);
      logCatalog('app', 'delete_catalog_app_ok', { id });
    } catch (err) {
      const message = catalogErrorMessage(err);
      logCatalog('error', 'delete_catalog_app_failed', { message, id });
      throw new Error(message);
    }
  },
};
